import { ETHERCAT_FEATURE_COMPRESSION } from './constants'
import { createHttpInstance } from './http'
import { compressAsBlob, isCompressionSupported } from './utils/compression'

export interface InterfaceEthercatConnectorSettings {
  protocol: 'http' | 'https'
  port: number
  timeout: number
  endpoints: {
    deployBus: string
    getConfig: string
    scanBus: string
    getEsiList: string
    getVersion: string
  }
  features: string[]
}

class EthercatConnector {
  private protocol: string
  private port: number
  private host: string
  private timeout: number
  private endpoints: InterfaceEthercatConnectorSettings['endpoints']
  private controllerFeatures: string[] = []

  public constructor(host: string, config: InterfaceEthercatConnectorSettings) {
    this.host = host
    this.port = config.port
    this.protocol = config.protocol
    this.timeout = config.timeout
    this.endpoints = config.endpoints
    this.controllerFeatures = config.features
  }

  public buildConnectionUrl(path: string) {
    return this.baseUrl(path)
  }

  public async getData(path: string) {
    const response = await this.httpClient.get(path)

    if (!response.data.data) {
      throw new Error('Empty response')
    }

    return response.data
  }

  public async postData(path: string, data: Record<string, any>) {
    let client = this.httpClient

    if (isCompressionSupported() && this.isControllerFeatureEnabled(ETHERCAT_FEATURE_COMPRESSION)) {
      client = createHttpInstance(this.baseUrl(), this.timeout, {
        headers: {
          'Content-Type': 'application/json',
          'Content-Encoding': 'gzip',
        },
      })

      data = await compressAsBlob(data)
    }

    const response = await client.post(path, data)
    return response.data
  }

  public getConfig() {
    return this.getData(this.endpoints.getConfig)
  }

  public scanBus() {
    return this.getData(this.endpoints.scanBus)
  }

  public deploy(data: Record<string, any>) {
    return this.postData(this.endpoints.deployBus, data)
  }

  public getEsiList(data: Record<string, any>) {
    return this.postData(this.endpoints.getEsiList, data)
  }

  public async getVersion() {
    return (await this.httpClient.get(this.endpoints.getVersion)).data
  }

  private get baseUrl() {
    return (path: string = '') => {
      const url = new URL(`${this.protocol}://${this.host}`)
      url.port = this.port.toString()
      url.pathname = path

      return url.toString()
    }
  }

  private get httpClient() {
    return createHttpInstance(this.baseUrl(), this.timeout)
  }

  private isControllerFeatureEnabled(feature: string) {
    return this.controllerFeatures.includes(feature)
  }
}

export default EthercatConnector
